<script setup lang="ts">
import type {
  CarbonEmission,
  ErrorDto,
  OfferCarbonEmissions,
  ResultsListProperties,
  SearchResult,
  SearchResults,
  ShoppingCart,
} from '@optionway/models';
import { isErrorDto } from '@optionway/models';
import { toCacheKey } from '@pinia/colada';
import { breakpointsTailwind } from '@vueuse/core';
import { toast } from 'vue-sonner';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.resultsList;
const apiUrl = $appConfig?.api?.url;

const { t } = useI18n();
const localePath = useLocalePath();

const properties = defineProps<ResultsListProperties>();

const searchCriteriaStore = useSearchCriteriaStore();
const searchResultIndexStore = useSearchResultIndexStore();
const searchResultFiltersStore = useSearchResultFiltersStore();
const currencyStore = useCurrencyStore();
const selectedSearchResultStore = useSelectedSearchResultStore();
const searchPricingStore = useSearchPricingStore();
const gtmStore = useGtmStore();
const sessionData = useSessionData();
// Gates the CO₂ query to client-only (after mount) so it never blocks SSR / first render.
const isMounted = useMounted();
const { queryLoading, filteringLoading } = useSearchResultsQueryState();
const { progress } = useFakeProgress(queryLoading);

const componentConfig = computed<ResultsListProperties>(() => {
  return {
    components: properties.components ?? config?.components,
    hideComponents: properties.hideComponents ?? config?.hideComponents,
  };
});

const isDevelopment = useMetaDevelopment();

const {
  status: mainStatus,
  error: mainError,
  isPending: mainIsPending,
  isLoading: mainIsLoading,
} = useQuery<SearchResults, ErrorDto, SearchResults>({
  // Committed key, never the live form: pinia-colada refetches on any key change while `enabled` is
  // true, so a form edit would run a search nobody asked for. Only search() moves it.
  key: () => ['search-results', toCacheKey([searchCriteriaStore.committedCriteriaKey])],
  query: () => {
    searchCriteriaStore.searchRequest.sessionData = sessionData.value;
    return $fetch<SearchResults>(`${apiUrl}/search`, { method: 'POST', body: searchCriteriaStore.toBody() });
  },
  // Fires only when explicitly armed by the search form (searchCriteriaStore.active = true).
  enabled: () => !!apiUrl && searchCriteriaStore.active,
  // staleTime: Infinity — cache is kept until form.vue explicitly invalidates it on next search,
  // preventing auto-refetch on criteria changes while the user is browsing results.
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
});

const {
  status: filteringStatus,
  error: filteringError,
  isLoading: filteringIsLoading,
} = useQuery<SearchResults, ErrorDto, SearchResults>({
  key: () => [
    'search-results',
    toCacheKey([searchCriteriaStore.committedCriteriaKey]),
    toCacheKey([searchResultFiltersStore.searchedFilteringCriteriaKey]),
  ],
  query: () => {
    return $fetch<SearchResults>(`${apiUrl}/search`, { method: 'POST', body: searchResultFiltersStore.toBody });
  },
  // Fires once the first debounced filter change commits the snapshot (legs.length > 0)
  // and the index store detects that filtering changes sort reference values (active = true).
  enabled: () =>
    !!apiUrl &&
    searchResultFiltersStore.searchedFilteringCriteriaKey.legs.length > 0 &&
    searchResultFiltersStore.active,
  // staleTime: Infinity — cache is kept until form.vue explicitly invalidates it on next search,
  // reusing cached results for repeated same-filter combinations without a new server call.
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
});

const { error: carbonError } = useQuery<OfferCarbonEmissions, ErrorDto, OfferCarbonEmissions>({
  // Keyed purely on offerUuids (globally unique): CO₂ is immutable per offer, so it is fetched once and
  // reused across searches/filters. offerUuidsMissingCarbon only shrinks → keys stay disjoint, each offer once.
  key: () => ['carbon-emission', searchResultIndexStore.offerUuidsMissingCarbon],
  query: () => {
    const body: CarbonEmission = {
      offerUuids: searchResultIndexStore.offerUuidsMissingCarbon,
      sessionData: sessionData.value,
    };
    return $fetch<OfferCarbonEmissions>(`${apiUrl}/search/carbon-emission`, { method: 'POST', body });
  },
  // Fires client-side once offers are displayed and some still lack CO₂; the index store overlays reactively.
  enabled: () => isMounted.value && !!apiUrl && searchResultIndexStore.offerUuidsMissingCarbon.length > 0,
  // Opt-in retry (plugin is off globally): retry only transient failures (network / 5xx), up to 3×.
  retry: (failureCount, error) => {
    const status = isErrorDto(error) ? error.statusCode : undefined;
    return (status === undefined || status >= 500) && failureCount < 3;
  },
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
});

// ONE pricing mutation for the whole list: useMutation creates a cache entry per call site, and the app
// never prices two offers at once — the rows learn which offer is in flight through props.
const {
  mutate: searchPricing,
  asyncStatus: pricingAsyncStatus,
  variables: pricingVariables,
} = useMutation<ShoppingCart, SearchResult, ErrorDto>({
  key: ['shopping-cart', 'flight'],
  onMutate: (result) => {
    selectedSearchResultStore.select(result);
    // On intent, while the offer is still in the rendered list its rank is resolved from.
    gtmStore.trackSelectItem(result.offerUuid);
  },
  mutation: (result) => {
    return $fetch<ShoppingCart>(`${apiUrl}/search/pricing`, {
      method: 'POST',
      body: selectedSearchResultStore.toBody(sessionData.value, result.offerUuid),
    });
  },
  onError: (error) => {
    console.error('SearchPricing error:', JSON.stringify(error));
    // Fixed id: a repeated pricing error replaces the existing toast instead of stacking a new one.
    toast.error(t('components.results.searchResult.pricingError'), { id: 'pricing-error' });
  },
  onSuccess: async (result) => {
    searchPricingStore.applyPricing(result);
    // Once the store holds the priced cart: view_item reads the confirmed fare, not the listed one.
    gtmStore.trackViewItem();
    await navigateTo(localePath('luggages'));
  },
});

// The offer being priced right now, undefined when idle — variables keeps the last call's offer after it settles.
const pricingOfferUuid = computed(() =>
  pricingAsyncStatus.value === 'loading' ? pricingVariables.value?.offerUuid : undefined,
);

// Same gate as useSearchResultsQueryState, read from this component's own useQuery: the list owns the
// cache entry, so reading it back through the cache (useQueryState) would loop render ↔ cache.
const loading = computed(() => searchCriteriaStore.searchRequested && (mainIsPending.value || mainIsLoading.value));

// Show error banner if the main query or the filtering query fails.
const error = computed(() => mainStatus.value === 'error' || filteringStatus.value === 'error');

const activateFilter = () => {
  // Skip while a BFF call is loading — searchedFilteringCriteriaKey serializes concurrent requests.
  if (filteringIsLoading.value) {
    return;
  }
  // Evaluate against the previous sort snapshot before overwriting it — same ordering as form.vue:
  // use the previous baseline for the decision, then commit the new snapshots.
  const willActivate = !searchResultFiltersStore.isImpossible && searchResultIndexStore.hasSortOptionsChanged();
  // Commit the new sort options baseline so the next evaluation compares correctly.
  searchResultIndexStore.snapshotSortOptions();
  searchResultIndexStore.snapshotFilteredCount();
  // Commit the new criteria key so getEntries and the filtering query use the stable snapshot.
  searchResultFiltersStore.snapshotFilteringCriteriaKey();
  // Arm the filtering query — pinia-colada fires once with the new key; settlement watch resets active.
  searchResultFiltersStore.active = willActivate;
};

watchDebounced(
  // toCacheKey prevents re-firing on same filter state (e.g. carrier prices refreshing after a BFF call).
  () => toCacheKey([searchResultFiltersStore.filteringCriteriaKey]),
  () => {
    activateFilter();
  },
  { debounce: 300 },
);

// Watches the fetch cycle, not the status: refetching an already-cached combination keeps its 'success'
// status all along (invalidate() marks the entry stale, it never drops data), so a status-only watch
// left `active` armed for good.
const mainSettled = computed(() => !mainIsLoading.value && mainStatus.value !== 'pending');

// Reset active once the main query settles — active is a one-shot trigger, not a permanent enabled flag.
watch(mainSettled, (settled) => {
  if (!settled) {
    return;
  }
  searchCriteriaStore.active = false;
  if (mainStatus.value === 'success') {
    gtmStore.trackResults();
  }
  // Deep-link: filters were pre-filled from URL before this component mounted,
  // so the debounced watcher never saw a change. Evaluate BFF eligibility once here.
  if (mainStatus.value === 'success' && searchResultFiltersStore.fromDeepLink) {
    searchResultFiltersStore.fromDeepLink = false;
    activateFilter();
  }
});

// Reset active once the filtering query settles — mirrors the main query watch above.
watch(
  () => filteringStatus.value,
  (status) => {
    if (status === 'success' || status === 'error') {
      searchResultFiltersStore.active = false;
    }
    // On settlement, not on the click: only now does search_results_count describe the filtered list.
    if (status === 'success') {
      gtmStore.trackFilterApplied();
    }
  },
);

if (isDevelopment) {
  watch([mainError, filteringError, carbonError], ([main, filtering, carbon]) => {
    if (main) {
      console.error('Search error:', main);
    }
    if (filtering) {
      console.error('Filtering error:', filtering);
    }
    if (carbon) {
      console.error('Carbon emission error:', carbon);
    }
  });
}

// Matches top-11 / md:h-11 on CommonFlightSummary (2.75rem × 16 = 44px)
const HEADER_H = 44;

const sidebarReference = useTemplateRef<HTMLElement>('sidebarReference');
const breakpoints = useBreakpoints(breakpointsTailwind);
const isLg = breakpoints.greaterOrEqual('lg');

let previousY = 0;
let currentTop = HEADER_H;
let rafId: number | undefined;

const updateSidebarPosition = () => {
  const element = sidebarReference.value;
  if (!element || !isLg.value) {
    return;
  }

  const y = window.scrollY;
  const winH = window.innerHeight;
  const elementH = element.offsetHeight;
  const delta = y - previousY;
  previousY = y;

  if (elementH <= winH - HEADER_H) {
    // Fits in the viewport: pinned to the top
    currentTop = HEADER_H;
  } else {
    // Taller than the viewport: follows the scroll, clamped between bottom-pinned and top-pinned
    const minTop = winH - elementH;
    currentTop = Math.min(HEADER_H, Math.max(minTop, currentTop - delta));
  }

  element.style.setProperty('--sidebar-top', `${currentTop}px`);
};

const scheduleUpdate = () => {
  if (rafId !== undefined || !isLg.value) {
    return;
  }
  rafId = requestAnimationFrame(() => {
    rafId = undefined;
    updateSidebarPosition();
  });
};

useEventListener('scroll', scheduleUpdate, { passive: true });
useResizeObserver(sidebarReference, scheduleUpdate);

const initializeSidebarPosition = () => {
  const element = sidebarReference.value;
  if (!element || !isLg.value) {
    return;
  }
  currentTop = HEADER_H;
  element.style.setProperty('--sidebar-top', `${HEADER_H}px`);
  element.style.setProperty('--sidebar-bottom', 'auto');
  previousY = window.scrollY;
};

watch(isLg, (lg) => {
  const element = sidebarReference.value;
  if (!element) return;
  if (lg) {
    initializeSidebarPosition();
  } else {
    element.style.removeProperty('--sidebar-top');
    element.style.removeProperty('--sidebar-bottom');
  }
});

onMounted(() => {
  initializeSidebarPosition();
});

onUnmounted(() => {
  if (rafId !== undefined) {
    cancelAnimationFrame(rafId);
    sidebarReference.value?.style.removeProperty('--sidebar-top');
    sidebarReference.value?.style.removeProperty('--sidebar-bottom');
  }
});
</script>

<template>
  <CommonErrorTechnicalProblem v-if="error" />
  <div
    v-else
    class="w-full p-4 md:p-6 lg:p-8"
  >
    <UiCustomContainer>
      <div class="flex flex-col gap-6 lg:flex-row">
        <div
          v-if="!componentConfig.hideComponents?.resultsFilters"
          class="hidden shrink-0 lg:block lg:w-65"
        >
          <aside
            ref="sidebarReference"
            :aria-label="$t('components.results.filters.label')"
            class="lg:sticky lg:pb-8 lg:sidebar-sticky"
          >
            <div class="flex h-16 flex-col justify-center gap-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1.5">
                  <span class="text-base font-semibold text-foreground">{{
                    $t('components.results.filters.label')
                  }}</span>
                  <Transition
                    enter-active-class="transition duration-200"
                    enter-from-class="scale-0 opacity-0"
                    enter-to-class="scale-100 opacity-100"
                    leave-active-class="transition duration-150"
                    leave-from-class="scale-100 opacity-100"
                    leave-to-class="scale-0 opacity-0"
                  >
                    <span
                      v-if="searchResultFiltersStore.activeFiltersCount > 0"
                      aria-live="polite"
                      :aria-label="`${searchResultFiltersStore.activeFiltersCount} ${$t('components.results.filters.activeFilters')}`"
                      class="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary-700 text-xs font-semibold text-text-inverse"
                    >
                      {{ searchResultFiltersStore.activeFiltersCount }}
                    </span>
                  </Transition>
                </div>
                <UiButton
                  v-if="searchResultFiltersStore.activeFiltersCount > 0"
                  variant="tertiary"
                  color="gray"
                  padding="none"
                  class="items-center border-0 text-xs font-normal leading-none text-text-secondary-600"
                  @click="searchResultFiltersStore.resetFilters"
                >
                  {{ $t('components.results.filters.reset') }}
                </UiButton>
              </div>
              <UiCustomSeparatorLine color-class="bg-gray-300" />
            </div>
            <ResultsFiltersVertical :loading="loading" />
          </aside>
        </div>

        <div class="lg:min-w-0 lg:grow">
          <div class="flex flex-col gap-4">
            <ResultsFiltersHorizontal
              v-if="!componentConfig.hideComponents?.resultsFilters"
              class="lg:hidden"
              :disabled="loading"
            />
            <div class="flex flex-col gap-0.5">
              <UiProgress
                :model-value="progress"
                class="h-1.5 w-full transition-opacity duration-300"
                :class="queryLoading ? 'opacity-100' : 'opacity-0'"
                indicator-class="bg-brand-primary-700"
              />
              <ResultsSortOptions
                v-if="!componentConfig.hideComponents?.resultsSortOptions"
                :disabled="queryLoading || (!loading && searchResultIndexStore.noResult)"
              />
            </div>
          </div>

          <ResultsNoResults
            v-if="!loading && searchResultIndexStore.noResult"
            class="mt-4"
          />

          <div
            v-else-if="!componentConfig.hideComponents?.resultsSearchResult"
            class="mt-4"
          >
            <ResultsAlmaBanner
              v-if="!componentConfig.hideComponents?.resultsAlmaBanner && currencyStore.isEuro"
              :installments-count="componentConfig.components?.resultsAlmaBanner?.installmentsCount"
              :min-amount-in-euro="componentConfig.components?.resultsAlmaBanner?.minAmountInEuro"
              :max-amount-in-euro="componentConfig.components?.resultsAlmaBanner?.maxAmountInEuro"
            />
            <ResultsResultCount />
            <div
              v-if="loading"
              class="flex w-full flex-col justify-center gap-4"
            >
              <div
                v-for="result in searchResultIndexStore.skeletonResults"
                :key="result.offerUuid"
              >
                <ResultsSearchResult v-bind="result" />
              </div>
            </div>
            <TransitionGroup
              v-else
              tag="div"
              class="relative flex w-full flex-col justify-center gap-4"
              move-class="transition-transform duration-500 ease-in-out"
              enter-active-class="transition-all duration-500 ease-out"
              enter-from-class="scale-95 opacity-0"
              enter-to-class="scale-100 opacity-100"
              leave-active-class="transition-all duration-500 ease-in"
              leave-from-class="scale-100 opacity-100"
              leave-to-class="scale-95 opacity-0"
            >
              <div
                v-for="result in searchResultIndexStore.availableSearchResults"
                :key="result.offerUuid"
              >
                <ResultsSearchResult
                  v-bind="result"
                  :pricing="pricingOfferUuid === result.offerUuid"
                  :select-disabled="pricingOfferUuid !== undefined && pricingOfferUuid !== result.offerUuid"
                  @select="searchPricing(result)"
                />
              </div>
            </TransitionGroup>
          </div>

          <div class="sticky bottom-8">
            <ResultsBackgroundSearchToast :loading="filteringLoading" />
          </div>
        </div>
      </div>
    </UiCustomContainer>
  </div>
</template>
